import { useState, useRef, useEffect, Children, isValidElement } from 'react';

import { CsControl, CsValue, CsArrow, CsDropdown, CsOption, CsSizer } from './CustomSelect.styled';

/**
 * v2 replacement for a native <select>. Parses the same <option> children so it
 * is a drop-in for the form Select, but renders a styled dropdown whose selected
 * option is highlighted in brand green. Values are coerced to strings to match
 * native <select> semantics (redux-form stores the same string it did before).
 *
 * Width: by default the control is as wide as its longest option; pass `width`
 * (number = px, or any CSS length) to fix it.
 */
export const CustomSelect = ({ input = {}, children, disabled, placeholder, width }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const options = Children.toArray(children)
    .filter(isValidElement)
    .map((c) => {
      const rawVal = c.props.value;
      const label = c.props.children;
      const value = rawVal !== undefined && rawVal !== null ? String(rawVal) : typeof label === 'string' ? label : '';
      return { value, label, disabled: c.props.disabled };
    });

  const currentValue = input.value !== undefined && input.value !== null ? String(input.value) : '';
  const current = options.find((o) => o.value === currentValue) || options[0];
  const shownLabel = current ? current.label : placeholder || '';

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (opt) => {
    if (opt.disabled) {
      return;
    }
    if (input.onChange) {
      input.onChange(opt.value);
    }
    if (input.onBlur) {
      input.onBlur(opt.value);
    }
    setOpen(false);
  };

  return (
    <CsControl ref={ref} className="cp-custom-select" $width={width}>
      <CsValue $open={open} $disabled={disabled} onClick={() => !disabled && setOpen((o) => !o)} className="custom-select-value">
        <span>
          {shownLabel}
          {!width && options.map((o, i) => <CsSizer key={i} aria-hidden="true">{o.label}</CsSizer>)}
        </span>
        <CsArrow $open={open} />
      </CsValue>
      {open && !disabled && (
        <CsDropdown role="listbox">
          {options.map((o, i) => (
            <CsOption key={i} role="option" aria-selected={o.value === currentValue} $selected={o.value === currentValue} $disabled={o.disabled} onClick={() => pick(o)}>
              {o.label}
            </CsOption>
          ))}
        </CsDropdown>
      )}
    </CsControl>
  );
};
